
import { defineComponent, ref, inject, onMounted } from 'vue';
import { Select, Radio } from '@/components/input-elements';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Apollo, Notify } from '@/core/services';
import { GET_POST_CODES } from '@/modules/system/post-codes/graphql/Queries';
import InnerLoader from '../../../../../components/InnerLoader.vue';

export default defineComponent({
    name: 'add new shipping method',
    components: {
        Radio,
        Select,
        InnerLoader
    },

    setup(props, context) {
        const emitter: any = inject('emitter');
        const postCodeFormRef = ref<null | HTMLFormElement>(null);
        const postCodes = ref([]) as Record<any, any>;
        const postCodeShippingLabel = ref([]) as Record<any, any>;
        const store = useStore();
        const i18n = useI18n();
        const optionRule = ref([]) as Record<any, any>;
        const cartRuleOption = ref([]) as Record<any, any>;
        const loader = ref(false);
        const postCodeRules = ref([]) as Record<any, any>;
        const postCodeRule = ref([]) as Record<any, any>;
        const postCodeCartOption = ref([]) as Record<any, any>;
        const showValidationErrors = ref(false);
        const previousPostCodeSelection = ref([]) as Record<any, any>;

        // Given Shipping Form Interface
        const postCodeFormData = ref({
            rule: [],
            cartRule: [],
            post_code_cost_no_rule: [],
            post_code_tax_no_rule: [],
            post_codes: [],
            post_code_cart_rules: [],
            post_code_cart_rules2: [],
            post_code_cart_cost: [],
            post_code_cart_cost2: [],
            post_code_tax: [],
            post_code_tax2: [],
            cartRulesData: {},
            noRuleCost: {},
            combinations: {}
        }) as any;

        cartRuleOption.value = [
            {
                label: 'message.WEIGHT message.RULE',
                value: 0,
                default: 0
            },
            {
                label: 'message.TOTAL message.RULE',
                value: 1,
                default: 0
            },
            {
                label: 'message.COMBINATIONS',
                value: 2,
                default: 0
            }
        ];

        optionRule.value = [
            {
                label: 'message.NO message.RULE',
                value: 0
            },
            {
                label: 'message.RULE',
                value: 1
            }
        ];

        const getpostCodeShippingRuleLabel = e => {
            e.forEach((element, index) => {
                if (element == 1) {
                    postCodeShippingLabel.value[index] = `${i18n.t('message.TOTAL')} ${i18n.t('message.RULE')}`;
                } else if (element == 2) {
                    postCodeShippingLabel.value[index] = `${i18n.t('message.COMBINATIONS')}`;
                } else {
                    postCodeShippingLabel.value[index] = `${i18n.t('message.WEIGHT')} ${i18n.t('message.RULE')}`;
                }
            });
        };

        const addRow = index => {
            if (!postCodeFormData.value.combinations) {
                postCodeFormData.value.combinations = {};
            }
            if (!postCodeFormData.value.combinations[index]) {
                postCodeFormData.value.combinations[index] = [];
            }

            postCodeFormData.value.combinations[index].push({
                firstValue: '',
                secondValue: '',
                price: '',
                tax: false
            });

            postCodeFormData.value.combinations = { ...postCodeFormData.value.combinations };
        };

        const removeRow = (ruleKey, rowIndex) => {
            if (postCodeFormData.value.combinations[ruleKey]) {
                postCodeFormData.value.combinations[ruleKey].splice(rowIndex, 1);

                if (postCodeFormData.value.combinations[ruleKey].length === 0) {
                    delete postCodeFormData.value.combinations[ruleKey];
                }

                postCodeFormData.value.combinations = { ...postCodeFormData.value.combinations };
            }
        };

        const getpostCodes = () => {
            loader.value = true;
            Apollo.watchQuery({
                query: GET_POST_CODES,
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'network-only',
                errorPolicy: 'all'
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                postCodes.value = [];
                data.get_post_codes.data.forEach(element => {
                    postCodes.value.push({
                        label: element.from + ' - ' + element.to,
                        value: element.id
                    });
                });
                loader.value = false;
            });
        };

        onMounted(() => {
            getpostCodes();
            // Initialize previous selection
            previousPostCodeSelection.value = [...postCodeFormData.value.post_codes];
        });

        const postCode = (groups: Record<any, any>) => {
            // Ensure groups is an array
            const currentSelection = Array.isArray(groups) ? groups : [];

            // Use stored previous selection to detect removals
            const previousSelection = [...previousPostCodeSelection.value];

            // Find removed post codes by comparing previous with current
            const removedPostCodes = previousSelection.filter(id => !currentSelection.includes(id));

            // Clean up data for removed post codes
            if (removedPostCodes.length > 0) {
                removedPostCodes.forEach(removedId => {
                    // Remove combinations
                    if (postCodeFormData.value.combinations && postCodeFormData.value.combinations[removedId]) {
                        delete postCodeFormData.value.combinations[removedId];
                    }

                    // Remove rules
                    if (postCodeRule.value[removedId] !== undefined) {
                        delete postCodeRule.value[removedId];
                    }

                    // Remove cart option
                    if (postCodeCartOption.value[removedId] !== undefined) {
                        delete postCodeCartOption.value[removedId];
                    }

                    // Remove shipping label
                    if (postCodeShippingLabel.value[removedId] !== undefined) {
                        delete postCodeShippingLabel.value[removedId];
                    }

                    // Remove cost and tax data
                    if (postCodeFormData.value.post_code_cost_no_rule[removedId] !== undefined) {
                        delete postCodeFormData.value.post_code_cost_no_rule[removedId];
                    }
                    if (postCodeFormData.value.post_code_tax_no_rule[removedId] !== undefined) {
                        delete postCodeFormData.value.post_code_tax_no_rule[removedId];
                    }
                    if (postCodeFormData.value.post_code_cart_rules[removedId] !== undefined) {
                        delete postCodeFormData.value.post_code_cart_rules[removedId];
                    }
                    if (postCodeFormData.value.post_code_cart_rules2[removedId] !== undefined) {
                        delete postCodeFormData.value.post_code_cart_rules2[removedId];
                    }
                    if (postCodeFormData.value.post_code_cart_cost[removedId] !== undefined) {
                        delete postCodeFormData.value.post_code_cart_cost[removedId];
                    }
                    if (postCodeFormData.value.post_code_cart_cost2[removedId] !== undefined) {
                        delete postCodeFormData.value.post_code_cart_cost2[removedId];
                    }
                    if (postCodeFormData.value.post_code_tax[removedId] !== undefined) {
                        delete postCodeFormData.value.post_code_tax[removedId];
                    }
                    if (postCodeFormData.value.post_code_tax2[removedId] !== undefined) {
                        delete postCodeFormData.value.post_code_tax2[removedId];
                    }
                });

                // Ensure reactivity for combinations
                postCodeFormData.value.combinations = { ...postCodeFormData.value.combinations };
            }

            let validate = false;

            // Only validate if adding a new post code (not when removing)
            if (currentSelection.length > previousSelection.length) {
                const selected_post_code = postCodeFormData.value.post_codes.find(ele => ele == currentSelection[currentSelection.length - 1]);
                const selected_post_codes = postCodes.value.find(ele => ele.value == selected_post_code);

                if (selected_post_codes) {
                    const selected_post_code_label = selected_post_codes.label.split(' - ');
                    const selected_from = selected_post_code_label[0];
                    const selected_to = selected_post_code_label[1];

                    for (let i = 0; i < currentSelection.length - 1; i++) {
                        const post_code = postCodeFormData.value.post_codes.find(ele => ele == currentSelection[i]);
                        const post_codes = postCodes.value.find(ele => ele.value == post_code);
                        if (post_codes) {
                            const post_code_label = post_codes.label.split(' - ');
                            const from = post_code_label[0];
                            const to = post_code_label[1];

                            if (
                                (from <= selected_from && to >= selected_from) ||
                                (from <= selected_to && to >= selected_to) ||
                                (selected_from <= from && selected_to >= from) ||
                                (selected_from <= to && selected_to >= to)
                            ) {
                                validate = true;
                            }
                        }
                    }
                }
            }

            if (validate) {
                postCodeFormData.value.post_codes.splice(currentSelection.length - 1, 1);
                Notify.error(`${i18n.t('message.POST_CODE_ALREADY_EXIST')}`);
            } else {
                if (currentSelection.length != 0) {
                    postCodeRules.value = [];
                    currentSelection.forEach(element => {
                        const post_codes = postCodes.value.find(ele => ele.value == element);
                        if (post_codes) {
                            postCodeRules.value.push({
                                relation: 'post-code',
                                data: post_codes
                            });
                        }
                    });
                } else {
                    postCodeRules.value = [];
                }
            }

            // Update previous selection for next comparison
            previousPostCodeSelection.value = [...currentSelection];
        };

        const validateForm = () => {
            postCodeFormRef.value?.validate(valid => {
                if (valid) {
                    const shippingpostCodeName = [] as Record<any, any>;
                    const validationErrors = [] as Record<any, any>;
                    showValidationErrors.value = false;

                    for (let i = 0; i < postCodeRules.value.length; i++) {
                        const ruleKey = postCodeRules.value[i].data.value;
                        const ruleType = postCodeRule.value[ruleKey];
                        const cartOption = postCodeCartOption.value[ruleKey];

                        if (ruleType === 1) {
                            if (cartOption === 2) {
                                // Validate combinations data
                                if (postCodeFormData.value.combinations[ruleKey]) {
                                    const combinationsForRule = postCodeFormData.value.combinations[ruleKey];

                                    for (let j = 0; j < combinationsForRule.length; j++) {
                                        const row = combinationsForRule[j];
                                        const first = Number(row.firstValue);
                                        const second = Number(row.secondValue);
                                        const price = Number(row.price);

                                        if (!first || first <= 0) {
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: j,
                                                message: `First value must be greater than 0 in row ${j + 1}`
                                            });
                                        }

                                        if (!second || second <= 0) {
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: j,
                                                message: `Second value must be greater than 0 in row ${j + 1}`
                                            });
                                        }

                                        if (!price || price <= 0) {
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: j,
                                                message: `Price must be greater than 0 in row ${j + 1}`
                                            });
                                        }

                                        if (second <= first && first > 0 && second > 0) {
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: j,
                                                message: `Second value must be greater than first value in row ${j + 1}`
                                            });
                                        }
                                    }

                                    const validRows = combinationsForRule
                                        .filter(r => Number(r.firstValue) > 0 && Number(r.secondValue) > Number(r.firstValue))
                                        .slice()
                                        .sort((a, b) => Number(a.firstValue) - Number(b.firstValue));

                                    for (let j = 0; j < validRows.length - 1; j++) {
                                        const currentRow = validRows[j];
                                        const nextRow = validRows[j + 1];
                                        if (Number(currentRow.secondValue) >= Number(nextRow.firstValue)) {
                                            const currentIndex = combinationsForRule.indexOf(currentRow);
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: currentIndex,
                                                message: `Range overlaps with next combination in row ${currentIndex + 1}`
                                            });
                                        }
                                    }
                                } else {
                                    validationErrors.push({
                                        ruleKey,
                                        message: `${i18n.t('message.PLEASE_ADD_AT_LEAST_ONE_COMBINATION_FOR')} ${postCodeRules.value[i].data.label}`
                                    });
                                }
                            } else if (cartOption === 0 || cartOption === 1) {
                                if (
                                    typeof postCodeFormData.value.post_code_cart_rules[ruleKey] === 'undefined' ||
                                    postCodeFormData.value.post_code_cart_rules[ruleKey] == '' ||
                                    typeof postCodeFormData.value.post_code_cart_rules2[ruleKey] === 'undefined' ||
                                    postCodeFormData.value.post_code_cart_rules2[ruleKey] == '' ||
                                    typeof postCodeFormData.value.post_code_cart_cost[ruleKey] === 'undefined' ||
                                    postCodeFormData.value.post_code_cart_cost[ruleKey] == '' ||
                                    typeof postCodeFormData.value.post_code_cart_cost2[ruleKey] === 'undefined' ||
                                    postCodeFormData.value.post_code_cart_cost2[ruleKey] == ''
                                ) {
                                    shippingpostCodeName.push(postCodeRules.value[i].data.label);
                                }
                            }
                        } else if (typeof postCodeFormData.value.post_code_cost_no_rule[ruleKey] === 'undefined' || postCodeFormData.value.post_code_cost_no_rule[ruleKey] == '') {
                            shippingpostCodeName.push(postCodeRules.value[i].data.label);
                        }
                    }

                    if (validationErrors.length > 0 || shippingpostCodeName.length > 0) {
                        showValidationErrors.value = true;

                        validationErrors.forEach(error => {
                            Notify.error(error.message);
                        });

                        if (shippingpostCodeName.length > 0) {
                            Notify.error(`${i18n.t('message.ENTER_RULES_AND_PRICE_FOR_SELECTED')} ${shippingpostCodeName.join(' , ')} ${i18n.t('message.SHIPPING')}`);
                        }

                        return;
                    }

                    const cartRulesData = {
                        post_codes: {
                            greater_condition: {
                                rules: postCodeFormData.value.post_code_cart_rules,
                                cost: postCodeFormData.value.post_code_cart_cost,
                                tax: postCodeFormData.value.post_code_tax
                            },
                            less_condition: {
                                rules: postCodeFormData.value.post_code_cart_rules2,
                                cost: postCodeFormData.value.post_code_cart_cost2,
                                tax: postCodeFormData.value.post_code_tax2
                            },
                            combinations: {
                                combinations: postCodeFormData.value.combinations
                            }
                        }
                    };

                    postCodeFormData.value.cartRulesData = cartRulesData;

                    const noRuleCost = {
                        post_code_cost: postCodeFormData.value.post_code_cost_no_rule,
                        post_code_tax: postCodeFormData.value.post_code_tax_no_rule
                    };

                    postCodeFormData.value.noRuleCost = noRuleCost;
                    postCodeFormData.value.rule = postCodeRule.value;
                    postCodeFormData.value.cartRule = postCodeCartOption.value;

                    context.emit('store', 'postCodeFormData', postCodeFormData.value);
                    return true;
                }

                return false;
            });
        };

        const resetForm = () => {
            emitter.emit('clearInput');
            postCodeFormRef.value?.resetFields();
        };

        const editShippingMethodValue = (shippingData: Record<any, any>) => {
            resetForm();
            if (shippingData.post_codes.length > 0) {
                shippingData.post_codes.forEach(element => {
                    postCodeFormData.value.post_codes.push(element.id);
                    postCodeRules.value.push({
                        relation: 'post-code',
                        data: { label: element.from + '-' + element.to, value: element.id }
                    });
                    shippingData.shipping_rule.forEach(ele => {
                        if (ele.relation == 'post-code' && ele.relation_id == element.id) {
                            switch (ele.condition) {
                                case '==':
                                    postCodeRule.value[element.id] = 0;
                                    postCodeFormData.value.post_code_cost_no_rule[element.id] = ele.shipping_cost.cost;
                                    postCodeFormData.value.post_code_tax_no_rule[element.id] = ele.shipping_cost.taxable == 0 ? false : true;
                                    break;
                                case '>=':
                                    postCodeRule.value[element.id] = 1;
                                    postCodeCartOption.value[element.id] = ele.rule == 'total' ? 1 : 0;
                                    postCodeShippingLabel.value[element.id] = postCodeCartOption.value[element.id] == 1 ? 'Total Rule' : 'Weight Rule';
                                    postCodeFormData.value.post_code_cart_cost[element.id] = ele.shipping_cost.cost;
                                    postCodeFormData.value.post_code_tax[element.id] = ele.shipping_cost.taxable == 0 ? false : true;
                                    postCodeFormData.value.post_code_cart_rules[element.id] = ele.cart_rule_value;
                                    break;
                                case '<':
                                    postCodeFormData.value.post_code_cart_cost2[element.id] = ele.shipping_cost.cost;
                                    postCodeFormData.value.post_code_tax2[element.id] = ele.shipping_cost.taxable == 0 ? false : true;
                                    postCodeFormData.value.post_code_cart_rules2[element.id] = ele.cart_rule_value;
                                    break;
                                case '<>':
                                    postCodeRule.value[element.id] = 1;
                                    postCodeCartOption.value[element.id] = ele.rule == 'between' ? 2 : 0;

                                    if (!postCodeFormData.value.combinations[element.id]) {
                                        postCodeFormData.value.combinations[element.id] = [];
                                    }

                                    postCodeFormData.value.combinations[element.id].push({
                                        firstValue: ele.first_value,
                                        secondValue: ele.second_value,
                                        price: ele.shipping_cost.cost,
                                        tax: ele.shipping_cost.taxable == 0 ? false : true
                                    });
                                    break;
                            }
                        }
                    });
                });
            }
        };

        return {
            postCodeFormData,
            postCodes,
            optionRule,
            cartRuleOption,
            postCodeFormRef,
            postCodeRules,
            postCodeRule,
            postCodeCartOption,
            postCodeShippingLabel,
            loader,
            editShippingMethodValue,
            validateForm,
            getpostCodeShippingRuleLabel,
            postCode,
            addRow,
            removeRow,
            showValidationErrors
        };
    }
});
